import { ScenarioScope } from '../scope';
import { ScenarioSummary } from '../types';

/** The models that cover the same people: a client's own, or one household's joint models. */
export type ModelGroup = {
  key: string;
  joint: boolean;
  clientIds: string[];
  primary: ScenarioSummary | null;
  // The primary first, then the rest as listed, with archived models last.
  models: ScenarioSummary[];
};

const coveredClientIds = (scenario: ScenarioSummary, scope: ScenarioScope): string[] =>
  scenario.familyId != null ? scenario.memberClientIds : [scenario.clientId ?? scope.id];

/** Joint models lead, so the household's plan is read before each person's own. */
export function groupModels(scenarios: ScenarioSummary[], scope: ScenarioScope): ModelGroup[] {
  const groups = new Map<
    string,
    { joint: boolean; clientIds: string[]; models: ScenarioSummary[] }
  >();
  for (const scenario of scenarios) {
    const joint = scenario.familyId != null;
    const clientIds = coveredClientIds(scenario, scope);
    const key = `${joint ? 'joint' : 'own'}:${[...clientIds].sort().join(',')}`;
    const group = groups.get(key) ?? { joint, clientIds, models: [] };
    group.models.push(scenario);
    groups.set(key, group);
  }
  return [...groups.entries()]
    .map(([key, { joint, clientIds, models }]) => {
      const primary = models.find(model => model.isPrimary && !model.archived) ?? null;
      const others = models.filter(model => model !== primary);
      return {
        key,
        joint,
        clientIds,
        primary,
        models: [
          ...(primary ? [primary] : []),
          ...others.filter(model => !model.archived),
          ...others.filter(model => model.archived),
        ],
      };
    })
    .sort((a, b) => Number(b.joint) - Number(a.joint));
}

/** The first names of the people a group covers, for those whose names are known. */
export const peopleIn = (group: ModelGroup, names: Record<string, string>): string[] =>
  group.clientIds.flatMap(id => (names[id] ? [names[id]] : []));
